/**
 * The backend list kernel's filter whitelist, and the three pure predicates
 * over it (#2167, extracted from the host's `services/list-request.ts`).
 *
 * The kernel answers HTTP 400 VALIDATION_FAILED for any field or op outside
 * the resource's `filterable` config, so every caller that builds or inspects
 * a request has to classify a `<field>=<op>:<value>` pair the same way. That
 * classification is pure — it reads a serialized value and a whitelist and
 * nothing else — which is why it lives here rather than beside the host's
 * request builder: the list controller, the UI's dropped-filter warning and
 * the persisted-filter sanitizer all need it, and none of them should have to
 * reach into a services layer to get it.
 *
 * `toApiListParams` (which renames `limit` to the kernel's reserved `size`)
 * stays in the host: that rename IS this app's wire contract, not a property
 * of a filter whitelist.
 */
import { OPERATOR_IDS, type OperatorId } from '@resource-kit/model';

/**
 * A resource's backend filter whitelist: field → the closed operator set
 * that field accepts. Mirrors the resource's backend
 * `*ListConfig.filterable` (`platform/http/list-query` `ListQueryConfig`).
 * The kernel returns HTTP 400 VALIDATION_FAILED for any field or op outside
 * it, so the adapter forwards only whitelisted filters and drops the rest.
 */
export type FilterWhitelist = Readonly<Record<string, readonly OperatorId[]>>;
export type SortWhitelist = readonly string[];
/**
 * The columns a list route's free-text `search` really matches (#1642).
 * EMPTY means the backend rejects `search` with 400 — the resource must not
 * offer a search box.
 */
export type SearchWhitelist = readonly string[];

const KNOWN_OPS: ReadonlySet<string> = new Set(OPERATOR_IDS);

/**
 * Recover the operator a serialized filter value encodes, mirroring the
 * kernel's `parseFilterValues`: a recognized leading `<op>:` prefix is that
 * op; anything else (a bare literal, or a value whose first colon segment
 * is not an operator — a timestamp, a URL) is `eq`.
 */
export function filterOp(value: string): OperatorId {
  const colon = value.indexOf(':');
  if (colon > 0) {
    const prefix = value.slice(0, colon).toLowerCase();
    if (KNOWN_OPS.has(prefix)) return prefix as OperatorId;
  }
  return 'eq';
}

/**
 * Whether `<field>=<value>` (value in the serialized `<op>:<value>` form)
 * would survive the backend whitelist: the field is present and the op the
 * value encodes is in its accepted set. The single source of truth reused
 * by `toApiListParams` (request build), `droppedFilterFields` (UI warning),
 * and the persisted-filter sanitizer in `useDataTableParams` (#1231).
 */
export function isWhitelisted(field: string, value: string, filterable: FilterWhitelist): boolean {
  const ops = filterable[field];
  return ops !== undefined && ops.includes(filterOp(value));
}

/**
 * The fields in `extraParams` that `toApiListParams` would DROP against
 * `filterable` (field absent, or its encoded op not in the field's set).
 * Same classification as `toApiListParams`, exposed so the UI can warn
 * the operator instead of the drop being silent — a dropped filter means
 * the visible chip/preset claims a filter the server never applied
 * (#1154). Returns [] when there is nothing to check.
 */
export function droppedFilterFields(
  extraParams: Record<string, string> | undefined,
  filterable?: FilterWhitelist,
): string[] {
  if (!extraParams || !filterable) return [];
  return Object.entries(extraParams)
    .filter(([field, value]) => !isWhitelisted(field, value, filterable))
    .map(([field]) => field);
}
